'use client';

import { signInWithGoogle } from '@repo/auth';
import { RainbowButton, toast } from '@repo/ui';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { GoogleIcon } from './google-icon';

interface IGoogleLoginButtonProps {
  onStart?: () => void;
}

export function GoogleLoginButton({ onStart }: IGoogleLoginButtonProps): React.ReactNode {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleGoogleLogin = (): void => {
    onStart?.();
    setIsLoading(true);

    const signInPromise = signInWithGoogle().then(
      (response: { error?: { message?: string; status?: number; code?: string } }) => {
        if (response.error) {
          const error = new Error(response.error.message || 'Unknown error');
          (error as Error & { code?: string; status?: number }).code = response.error.code;
          (error as Error & { code?: string; status?: number }).status = response.error.status;
          throw error;
        }
        return response;
      },
    );

    toast.promise(signInPromise, {
      loading: 'Redirecting to Google...',
      success: 'Redirecting...',
      error: (err: Error & { code?: string; status?: number }) => {
        setIsLoading(false);
        const details = [err.code, err.status ? `Status: ${err.status}` : null]
          .filter(Boolean)
          .join(' - ');
        return `Failed to sign in with Google${details ? ` (${details})` : ''}: ${err.message}`;
      },
    });
  };

  return (
    <RainbowButton
      type="button"
      variant="outline"
      size="lg"
      className="w-full max-w-xs gap-2"
      onClick={handleGoogleLogin}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <GoogleIcon className="size-4 shrink-0" aria-hidden="true" />
      )}
      Continue with Google
    </RainbowButton>
  );
}
